import { useNavigate } from "react-router-dom"
import { Skeleton } from "@heroui/react"
import { useRecentTasks } from "@src/presentation/hooks/task"
import { formatDateHuman } from "@src/shared/utils/date-formatter"
import { usePermission } from "@src/presentation/hooks/usePermission"
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder"

interface TasksSummaryCardProps {
    limit?: number
    stats?: {
        total: number;
        pending: number;
        processing: number;
        completed: number;
        failed: number;
    }
}

export function TasksSummaryCard({ limit = 5, stats: externalStats }: TasksSummaryCardProps) {
    const navigate = useNavigate()
    const { hasPermission } = usePermission()
    const canViewTasks = hasPermission("task:read")

    // Early return if no permission
    if (!canViewTasks) {
        return <PermissionPlaceholder title="Recent Tasks" description="You need 'task:read' permission to view tasks." />
    }

    const { tasks: displayTasks, stats: calculatedStats, isLoading, error } = useRecentTasks(limit)

    // Use external stats if provided, otherwise use calculated stats
    const stats = externalStats || calculatedStats

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "completed":
                return "bg-success/10 text-success border-success/20"
            case "pending":
                return "bg-warning/10 text-warning border-warning/20"
            case "processing":
                return "bg-blue-500/10 text-blue-500 border-blue-500/20"
            case "failed":
                return "bg-destructive/10 text-destructive border-destructive/20"
            default:
                return "bg-muted/10 text-muted-foreground border-muted/20"
        }
    }

    if (error) {
        return (
            <div className="rounded border border-border bg-secondary p-6">
                <p className="text-destructive">Error loading tasks: {error.message}</p>
            </div>
        )
    }

    return (
        <div className="rounded border border-border bg-card shadow-none hover:shadow-custom transition-shadow">
            {/* Header with Stats */}
            <div className="border-b border-border p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-accent/10">
                            <svg className="h-5 w-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">Processing Tasks</h2>
                            <p className="text-sm text-muted-foreground">PASCO processing pipeline</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/tasks")}
                        className="rounded border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                    >
                        View All ({stats.total})
                    </button>
                </div>

                {/* Quick Stats */}
                {isLoading ? (
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="rounded border border-border p-3">
                                <Skeleton className="h-4 w-20 rounded mb-2" />
                                <Skeleton className="h-7 w-12 rounded" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div className="rounded border border-success/10 bg-success/5 p-3">
                            <p className="text-xs text-muted-foreground">Completed</p>
                            <p className="text-2xl font-bold text-success">{stats.completed}</p>
                        </div>
                        <div className="rounded border border-blue-500/10 bg-blue-500/5 p-3">
                            <p className="text-xs text-muted-foreground">Processing</p>
                            <p className="text-2xl font-bold text-blue-500">{stats.processing}</p>
                        </div>
                        <div className="rounded border border-warning/10 bg-warning/5 p-3">
                            <p className="text-xs text-muted-foreground">Pending</p>
                            <p className="text-2xl font-bold text-warning">{stats.pending}</p>
                        </div>
                        <div className="rounded border border-destructive/10 bg-destructive/5 p-3">
                            <p className="text-xs text-muted-foreground">Failed</p>
                            <p className="text-2xl font-bold text-destructive">{stats.failed}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Recent Tasks List */}
            <div className="divide-y divide-border">
                {isLoading ? (
                    <div className="p-4 space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center justify-between">
                                <div className="space-y-2 flex-1">
                                    <Skeleton className="h-4 w-32 rounded" />
                                    <Skeleton className="h-3 w-48 rounded" />
                                </div>
                                <Skeleton className="h-6 w-20 rounded-full" />
                            </div>
                        ))}
                    </div>
                ) : displayTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <svg
                            className="h-12 w-12 text-muted-foreground/50"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                        </svg>
                        <p className="mt-2 text-sm text-muted-foreground">No tasks found</p>
                    </div>
                ) : (
                    displayTasks.map((task) => (
                        <div
                            key={task.id}
                            className="flex items-center justify-between p-4 transition-colors hover:bg-muted/50 cursor-pointer"
                            onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <code className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-foreground">
                                            {task.id.slice(0, 8)}...
                                        </code>
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate">
                                        PASCO: {task.pascoId.slice(0, 8)}...
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Updated: {formatDateHuman(task.updatedAt)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                                {task.attemptCount > 0 && (
                                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                                        {task.attemptCount} {task.attemptCount === 1 ? "attempt" : "attempts"}
                                    </span>
                                )}
                                <span
                                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${getStatusColor(
                                        task.status
                                    )}`}
                                >
                                    {task.status}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}
